import {
  OfferPublished,
  OfferStatus,
  OfferSummaryCount,
} from "graphql/__generated__/globalTypes";

import { FC } from "react";
import OfferCard from "./PublishedOfferCard";
import CancelledOfferCard from "./CancelledOfferCard";
import { SearchInput } from "components";
import { Tooltip } from "@mantine/core";
import Loader from "components/layout/loader";

interface OfferCardGridProps {
  offers: OfferPublished[];
  searchValue: string;
  search: (value: string) => void;
  selected: string[];
  loading: boolean;
  error?: Error;
  refresh?: () => void;
  renderAction?: React.ReactNode;
  filterStatus: "TODAY" | "MISSED" | "CANCELLED";
  onFilterStatusChange: (status: "TODAY" | "MISSED" | "CANCELLED") => void;
  subFilterStatus: "All" | OfferStatus.Open | OfferStatus.Pending;
  summaryCount: OfferSummaryCount;
  summaryLoading: boolean;
}

const OfferCardGrid: FC<OfferCardGridProps> = ({
  offers,
  searchValue,
  search,
  selected,
  loading,
  refresh,
  error,
  renderAction,
  filterStatus,
  onFilterStatusChange,
  subFilterStatus,
  summaryCount,
  summaryLoading,
}) => {
  const getTodaysDisplayCount = () => {
    return summaryLoading ? "..." : summaryCount.todays_offers;
  };

  const getMissedDisplayCount = () => {
    return summaryLoading ? "..." : summaryCount.missed_offers;
  };

  const getFilterTitle = () => {
    if (filterStatus === "CANCELLED") {
      return "Cancelled Offers";
    }
    const baseTitle =
      filterStatus === "TODAY" ? "Today's Offers" : "Missed Offers";
    const subTitle = subFilterStatus === "All" ? "" : ` (${subFilterStatus})`;
    return `${baseTitle}${subTitle}`;
  };

  const renderOfferCards = () => {
    if (offers.length === 0) return null;

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 items-start lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {offers.map((offer: any) => {
          // Check if this is a cancelled offer (has offer_cancel_reason)
          if (filterStatus === "CANCELLED" || offer.offer_cancel_reason) {
            return (
              <CancelledOfferCard
                key={offer.offer_id || offer.id}
                offer={offer}
              />
            );
          }

          const hasParticipated =
            !!offer.reinsurer_participation &&
            offer.reinsurer_participation?.offer_participant_percentage !== 0;

          return (
            <OfferCard
              key={offer.offer?.offer_id || offer.offer_id}
              offer={offer}
              hasParticipated={hasParticipated}
              currentParticipation={offer.reinsurer_participation || undefined}
              refetchOffers={refresh}
              selected={selected.includes(offer.offer?.offer_id || offer.offer_id || '')}
            />
          );
        })}
      </div>
    );
  };

  const shouldShowNoResults = offers.length === 0;

  const renderSegmentedControlWithTooltips = () => {
    return (
      <div className="inline-flex rounded-md bg-gray-100 p-1">
        <Tooltip
          label="Offers that have been published and are under 24 hours old"
          position="top"
          withArrow
          multiline
          width={220}
        >
          <button
            onClick={() => onFilterStatusChange("TODAY")}
            className={`
              px-3 py-2 text-sm font-medium rounded-md transition-all
              ${filterStatus === "TODAY"
                ? "bg-green-600 text-white shadow-sm"
                : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
              }
            `}
          >
            Today's Offers ({getTodaysDisplayCount()})
          </button>
        </Tooltip>

        <Tooltip
          label="Offers that are over 24 hours and you didn't participate during that window"
          position="top"
          withArrow
          multiline
          width={220}
        >
          <button
            onClick={() => onFilterStatusChange("MISSED")}
            className={`
              px-3 py-2 text-sm font-medium rounded-md transition-all ml-1
              ${filterStatus === "MISSED"
                ? "bg-green-600 text-white shadow-sm"
                : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
              }
            `}
          >
            Missed Offers ({getMissedDisplayCount()})
          </button>
        </Tooltip>

        <Tooltip
          label="Offers that have been cancelled by the broker"
          position="top"
          withArrow
          multiline
          width={220}
        >
          <button
            onClick={() => onFilterStatusChange("CANCELLED")}
            className={`
              px-3 py-2 text-sm font-medium rounded-md transition-all ml-1
              ${filterStatus === "CANCELLED"
                ? "bg-gray-600 text-white shadow-sm"
                : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
              }
            `}
          >
            Cancelled Offers
          </button>
        </Tooltip>
      </div>
    );
  };
  return (
    <div className="w-full">
      <div className="flex flex-wrap items-center justify-between mb-6 gap-4">
        <SearchInput
          placeholder="Search offers..."
          value={searchValue}
          onChange={search}
          className="w-64"
        />

        <div className="flex flex-wrap gap-4 items-center">
          {renderSegmentedControlWithTooltips()}
          <div className="flex space-x-4">{renderAction}</div>
        </div>
      </div>

      {loading ? (
        <Loader card />
      ) : error ? (
        <div className="bg-red-50 p-4 rounded-md">
          <p className="text-red-700">Error loading offers: {error.message}</p>
        </div>
      ) : shouldShowNoResults ? (
        <div className="bg-gray-50 p-8 rounded-md flex flex-col items-center justify-center h-64">
          <p className="text-gray-500 text-lg">No offers found</p>
          <p className="text-gray-400">
            Try adjusting your filters or search terms
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          <div>
            <div className="mb-4 pb-2 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-800 flex items-center">
                <span
                  className={`inline-block w-2 h-2 rounded-full mr-2 ${
                    filterStatus === "TODAY"
                      ? "bg-green-500"
                      : filterStatus === "CANCELLED"
                      ? "bg-gray-500"
                      : "bg-yellow-500"
                  }`}
                ></span>
                {getFilterTitle()} ({offers.length})
              </h2>
            </div>
            {renderOfferCards()}
          </div>
        </div>
      )}
    </div>
  );
};

export default OfferCardGrid;
